import { AxiosRequestConfig, AxiosResponse } from 'axios';

import { Config } from '../config';
import Client from './client';
import Storage from './storage';
import { createAuthorizationHeader, createUrl } from './utils';

type ApiOptions = {
  requiresAuthentication?: boolean;
  body?: AxiosRequestConfig['data'];
  headers?: Record<string, string>;
  method?: AxiosRequestConfig['method'];
  queryParams?: Record<string, string>;
};

export type StorageToken = { accessToken: string; refreshToken: string };

export class Api {
  private storage = new Storage();

  private accessTokenKey = Config.AccessTokenKey;
  private refreshTokenKey = Config.RefreshTokenKey;
  private accessTokenFallbackKeys = ['hypercite.access_token'];
  private refreshTokenFallbackKeys = ['hypercite.refresh_token'];
  private authUrl = Config.AuthApiUrl;
  private baseUrl = Config.BaseApiUrl;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async request<T = any>(uri: string, options: ApiOptions = {}): Promise<AxiosResponse<T>> {
    const {
      requiresAuthentication,
      body: data,
      headers = {},
      method = 'get',
      queryParams,
    } = options;
    const url = createUrl(uri, requiresAuthentication ? this.baseUrl : this.authUrl, queryParams);
    const config: AxiosRequestConfig = { url, data, method, headers };

    if (requiresAuthentication && !headers.Authorization) {
      const tokens = await this.getToken();
      config.headers = createAuthorizationHeader(headers, tokens.accessToken);
    }

    const response = await Client.v1(config);
    return response;
  }

  private async getTokenValue(primaryKey: string, fallbackKeys: string[]): Promise<string> {
    try {
      return await this.storage.getItem<string>(primaryKey);
    } catch (_err) {
      for (const key of fallbackKeys) {
        try {
          const token = await this.storage.getItem<string>(key);
          // Migrate legacy key to the primary key expected by this frontend.
          await this.storage.setItem(primaryKey, token);
          return token;
        } catch (_legacyErr) {
          // Try next key
        }
      }
      throw new Error(`No value found for key: ${primaryKey}`);
    }
  }

  async getToken(): Promise<{ accessToken: string; refreshToken?: string }> {
    const accessToken = await this.getTokenValue(this.accessTokenKey, this.accessTokenFallbackKeys);
    let refreshToken: string | undefined;
    try {
      refreshToken = await this.getTokenValue(this.refreshTokenKey, this.refreshTokenFallbackKeys);
    } catch (_err) {
      refreshToken = undefined;
    }

    return { accessToken, refreshToken };
  }

  removeToken(): Promise<[void, void]> {
    return Promise.all([
      this.storage.removeItem(this.accessTokenKey),
      this.storage.removeItem(this.refreshTokenKey),
    ]);
  }

  async setToken({ token }: { token: StorageToken }): Promise<StorageToken> {
    await this.storage.setItem(this.accessTokenKey, token.accessToken);

    if (token.refreshToken) {
      await this.storage.setItem(this.refreshTokenKey, token.refreshToken);
    }

    return token;
  }
}

const api = new Api();

export { api };
